/*
 This slice is a catchall for various bits of game state
*/
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import gameConfig from '@content/gameconfig';
import { SoundsDataKeys } from '../../../types';

type HeaderTypeProps = 'intro' | 'minimal' | 'recall' | 'full' | 'shareable';
type Notification = {
  id: number;
  text: string;
};

export type GameState = {
  /** initialized to true once you start the game */
  hasStarted: boolean;
  hasFinished: boolean;
  /** what kind of fixed header the user is seeing currently */
  headerType: HeaderTypeProps;
  /** sound settings */
  isBackgroundMusicOn: boolean;
  areSoundEffectsOn: boolean;
  /** language setting, we cannot really reasonably infer this as its dynamic, technically the current language code could be Klingon for all we know  */
  currentLanguage: string;
  /** this just keeps track of our notifications in the game, this array is usually 0 until you get notifications, the default behaviour of our app is that notifications disappear after a while */
  languageCodesUsed: string[];
  notifications: Notification[];
  defaultLanguage: string;
  isPairShareEnabled: boolean;
}

const langsUsed = gameConfig.Languages_Used;
// make room to set this via env in case we want to host same base site on diff localized urls
export const firstLanguage =
  process.env.GATSBY_DEFAULT_LANG ||
  langsUsed?.[0]?.Content_Type ||
  langsUsed?.Content_Type;

export const initialGameState: GameState = {
  hasStarted: false,
  hasFinished: false,
  headerType: 'intro',
  isBackgroundMusicOn: false,
  areSoundEffectsOn: false,
  defaultLanguage: firstLanguage,
  currentLanguage: firstLanguage,
  languageCodesUsed:
    langsUsed?.length > 0
      ? langsUsed.map(l => l.Content_Type)
      : [langsUsed?.Content_Type],
  notifications: [],
  isPairShareEnabled: false,
};

let internalNotificationCount = 0;

export const gameStateSlice = createSlice({
  name: 'gameState',
  initialState: initialGameState,
  reducers: {
    resetGame: state => ({
      ...initialGameState,
      // keep our settings/lang stable
      isBackgroundMusicOn: state.isBackgroundMusicOn,
      areSoundEffectsOn: state.areSoundEffectsOn,
      currentLanguage: state.currentLanguage,
      isPairShareEnabled: state.isPairShareEnabled,
    }),
    gameHasStarted: state => {
      state.hasStarted = true;
    },
    gameHasFinished: state => {
      state.hasFinished = true;
      state.isPairShareEnabled = false;
    },
    setHeaderType: (
      state,
      action: PayloadAction<{ headerType: HeaderTypeProps }>,
    ) => {
      state.headerType = action.payload.headerType;
    },
    playSound: {
      reducer: (state, action: PayloadAction<{ sound: SoundsDataKeys }>) => {
        // state.isBackgroundMusicOn = action.payload.sound;
      },
      prepare: payload => ({
        payload,
        meta: {
          sound: {
            play: payload.sound,
          },
        },
      }),
    },

    setIsBackgroundMusicOn: {
      reducer: (state, action: PayloadAction<{ setTo: boolean }>) => {
        state.isBackgroundMusicOn = action.payload.setTo;
      },
      prepare: payload => {
        let soundEffect;
        if (payload.setTo) {
          soundEffect = { play: 'Music' };
        } else {
          soundEffect = { stop: 'Music' };
        }
        return {
          payload,
          meta: {
            sound: {
              ...soundEffect,
            },
          },
        };
      },
    },
    setAreSoundEffectsOn: (
      state,
      action: PayloadAction<{ setTo: boolean }>,
    ) => {
      state.areSoundEffectsOn = action.payload.setTo;
    },
    setCurrentLanguage: (
      state,
      action: PayloadAction<{ setTo: string; eventType?: string }>,
    ) => {
      state.currentLanguage = action.payload.setTo;
    },
    addNewNotification: (state, action: PayloadAction<string>) => {
      state.notifications = [
        ...state.notifications,
        {
          id: (internalNotificationCount += 1),
          text: action.payload,
        },
      ];
    },
    removeAllNotifications: state => {
      state.notifications = [];
    },
    setIsPairShareEnabled: (state, action: PayloadAction<{ setTo: boolean }>) => {
        state.isPairShareEnabled = action.payload.setTo;
    }
  },
});

// Action creators are generated for each case reducer function
export const {
  gameHasStarted,
  gameHasFinished,
  resetGame,
  setHeaderType,
  setIsBackgroundMusicOn,
  setCurrentLanguage,
  setAreSoundEffectsOn,
  playSound,
  removeAllNotifications,
  addNewNotification,
  setIsPairShareEnabled,
} = gameStateSlice.actions;

export default gameStateSlice.reducer;
